'use client';

import { useToast } from '@common/hooks';
import { CustomError } from '@core/utils';
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';

import { AuthMutation, AuthQueryOptions } from '@/api';

import { parseKstDateToMs } from '../../_utils/countdown';

export interface CoupangEarnData {
  /** 서버가 내려주는 재지급 가능 시각 epoch ms. 지금 지급 가능하면 null */
  nextAvailableAtMs: number | null;
  /** 이번 참여 시 지급되는 포인트. 서버가 안 내려주면 null */
  rewardPoint: number | null;
}

//* 도메인 쿨다운(재참여 불가) 에러 코드. wire status는 200이며 return_code로 판정한다.
const COOLDOWN_RETURN_CODE = 30400;

export const useCoupangEarn = () =>
  useSuspenseQuery({
    ...AuthQueryOptions['get:/wello/v2/auth/point/coupang-banner/status'](),
    select: (response): CoupangEarnData => ({
      nextAvailableAtMs: response.context?.nextAvailableAt
        ? parseKstDateToMs(response.context.nextAvailableAt)
        : null,
      rewardPoint: response.context?.rewardPoint ?? null,
    }),
  });

export const useClaimCoupang = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    ...AuthMutation['post:/wello/v2/auth/point/coupang-banner'](),
    //* invalidate Promise를 반환해 refetch 완료까지 isPending을 유지한다 —
    //* 새 쿨다운이 도착하기 전 버튼이 "받기 가능"으로 되살아나는 거짓 상태 창 제거.
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey:
            AuthQueryOptions['get:/wello/v2/auth/point/coupang-banner/status']()
              .queryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: AuthQueryOptions['get:/wello/v2/auth/point']().queryKey,
        }),
      ]),
    onError: (error) => {
      if (
        error instanceof CustomError &&
        error.return_code === COOLDOWN_RETURN_CODE
      ) {
        toast({ message: '아직 재참여할 수 없어요', type: 'fail' });

        return queryClient.invalidateQueries({
          queryKey:
            AuthQueryOptions['get:/wello/v2/auth/point/coupang-banner/status']()
              .queryKey,
        });
      }

      toast({
        message: '적립에 실패했어요. 다시 시도해 주세요.',
        type: 'fail',
      });
    },
  });
};
